import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ThemeBucket, ThemeListItem } from '@playerbase/types';
import { cn } from '@playerbase/ui';

import { MetricChip } from './metric-chip.js';
import { PatternCard } from './pattern-card.js';

// Pattern list for the Insights page. Master-detail layout: a rail on
// the left lists the six bucket categories (the four canonical buckets
// plus the simmering/none pseudo-bucket plus dismissed), grouped by
// sentiment; the right pane shows all patterns for the selected rail
// item using the same PatternCard UI used elsewhere.
//
// Selection lives in the URL (?bucket=…) so deep links work and reload
// preserves the active view. The shape mirrors the Feedback stream and
// Playtests layouts.
//
// Within a bucket the server sort (last_member_added_at desc,
// first_seen_at desc) is preserved; we only partition, never re-sort.

// Rail key = the canonical buckets plus the two pseudo-buckets the rail
// surfaces. `simmering` resolves to bucket `none`; `dismissed` resolves
// to the `status === 'dismissed'` filter (which spans every bucket, so
// the bucket value of a dismissed theme is irrelevant once it lands).
type RailKey =
  | 'critical'
  | 'emerging'
  | 'standout'
  | 'trending'
  | 'simmering'
  | 'dismissed';

const RAIL_KEYS: readonly RailKey[] = [
  'critical',
  'emerging',
  'standout',
  'trending',
  'simmering',
  'dismissed',
];

function isRailKey(value: string | null): value is RailKey {
  return value !== null && (RAIL_KEYS as readonly string[]).includes(value);
}

function railLabel(key: RailKey): string {
  if (key === 'critical') return 'Critical';
  if (key === 'emerging') return 'Emerging';
  if (key === 'standout') return 'Standout';
  if (key === 'trending') return 'Trending';
  if (key === 'simmering') return 'Simmering';
  return 'Dismissed';
}

export interface ThemesListProps {
  themes: ThemeListItem[];
  totalMessages: number;
  // Active studio + game slugs. Piped through to PatternCard so the
  // "View in feed" mention link can branch between /feedback?focus=<id>
  // (messages, polls, forms, threads) and the playtest session detail
  // page based on the aspect's source.
  studioShortId: string;
  gameShortId: string;
  // Fired after a successful studio intervention on any card so the
  // page can refetch the themes list (alongside the realtime echo).
  onChanged?: (() => void) | undefined;
}

interface SectionHeadingProps {
  children: string;
}

function SectionHeading({ children }: SectionHeadingProps): JSX.Element {
  return (
    <p className="px-3 pt-4 pb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
      {children}
    </p>
  );
}

interface RailItemProps {
  railKey: RailKey;
  count: number;
  selected: boolean;
  onSelect: (key: RailKey) => void;
}

function RailItem({ railKey, count, selected, onSelect }: RailItemProps): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(railKey)}
        aria-pressed={selected}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-sm px-3 py-2 text-sm transition-colors duration-150',
          'border-l-2',
          selected
            ? 'border-l-accent bg-accent-bg text-accent'
            : 'border-l-transparent text-text-muted hover:bg-surface-2 hover:text-text',
        )}
      >
        <span>{railLabel(railKey)}</span>
        <MetricChip>{count}</MetricChip>
      </button>
    </li>
  );
}

export function ThemesList({
  themes,
  totalMessages,
  studioShortId,
  gameShortId,
  onChanged,
}: ThemesListProps): JSX.Element {
  const [params, setParams] = useSearchParams();
  const railParam = params.get('bucket');
  const selected: RailKey = isRailKey(railParam) ? railParam : 'critical';

  // Partition once. dismissedThemes is its own pane; active themes feed
  // the four buckets + simmering. allThemes passed to PatternCard stays
  // active-only because the merge picker requires both sides active
  // (see migration 0097 / find_nearest_theme).
  const { activeThemes, dismissedThemes, byBucket, counts } = useMemo(() => {
    const active: ThemeListItem[] = [];
    const dismissed: ThemeListItem[] = [];
    for (const t of themes) {
      if (t.status === 'dismissed') dismissed.push(t);
      else active.push(t);
    }
    const grouped = new Map<ThemeBucket, ThemeListItem[]>();
    for (const t of active) {
      const list = grouped.get(t.bucket);
      if (list === undefined) grouped.set(t.bucket, [t]);
      else list.push(t);
    }
    const c: Record<RailKey, number> = {
      critical: (grouped.get('critical') ?? []).length,
      emerging: (grouped.get('emerging') ?? []).length,
      standout: (grouped.get('standout') ?? []).length,
      trending: (grouped.get('trending') ?? []).length,
      simmering: (grouped.get('none') ?? []).length,
      dismissed: dismissed.length,
    };
    return { activeThemes: active, dismissedThemes: dismissed, byBucket: grouped, counts: c };
  }, [themes]);

  function selectRail(key: RailKey): void {
    const next = new URLSearchParams(params);
    next.set('bucket', key);
    // replace: intra-page clicks shouldn't pollute the back-button stack.
    setParams(next, { replace: true });
  }

  if (themes.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        No patterns detected yet. They&apos;ll appear as feedback comes in.
      </p>
    );
  }

  // Right-pane source. Dismissed spans every bucket so it's its own
  // bucket-by-status filter; simmering is the alias for `none`.
  let paneThemes: ThemeListItem[];
  if (selected === 'dismissed') paneThemes = dismissedThemes;
  else if (selected === 'simmering') paneThemes = byBucket.get('none') ?? [];
  else paneThemes = byBucket.get(selected) ?? [];

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_minmax(0,1fr)]">
      <nav
        aria-label="Pattern buckets"
        className="rounded-md border border-border bg-surface md:sticky md:top-4 md:self-start"
      >
        <SectionHeading>Negative</SectionHeading>
        <ul className="px-2">
          <RailItem
            railKey="critical"
            count={counts.critical}
            selected={selected === 'critical'}
            onSelect={selectRail}
          />
          <RailItem
            railKey="emerging"
            count={counts.emerging}
            selected={selected === 'emerging'}
            onSelect={selectRail}
          />
        </ul>
        <SectionHeading>Positive</SectionHeading>
        <ul className="px-2">
          <RailItem
            railKey="standout"
            count={counts.standout}
            selected={selected === 'standout'}
            onSelect={selectRail}
          />
          <RailItem
            railKey="trending"
            count={counts.trending}
            selected={selected === 'trending'}
            onSelect={selectRail}
          />
        </ul>
        <ul className="mt-3 border-t border-border px-2 pt-3 pb-3">
          <RailItem
            railKey="simmering"
            count={counts.simmering}
            selected={selected === 'simmering'}
            onSelect={selectRail}
          />
          <RailItem
            railKey="dismissed"
            count={counts.dismissed}
            selected={selected === 'dismissed'}
            onSelect={selectRail}
          />
        </ul>
      </nav>

      <section
        aria-label={`${railLabel(selected)} patterns`}
        className="flex min-w-0 flex-col gap-3"
      >
        <header className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-text">
            {railLabel(selected)} <MetricChip>{paneThemes.length}</MetricChip>
          </h3>
        </header>
        {paneThemes.length === 0 ? (
          <p className="rounded-md border border-border bg-surface p-4 text-xs text-text-muted">
            Nothing here yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {paneThemes.map((theme) => (
              <li key={theme.id}>
                <PatternCard
                  pattern={theme}
                  totalMessages={totalMessages}
                  studioShortId={studioShortId}
                  gameShortId={gameShortId}
                  allThemes={activeThemes}
                  onChanged={onChanged}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
